<div class="flex flex-column gap-3 h-full">

    @switch (state.phase) {

        @case ('empty') {
            <p-message severity="info" styleClass="w-full">
                <div class="flex flex-column gap-1">
                    <span class="font-semibold">Noch keine Abfrage definiert.</span>
                    <span>
                        Wählen Sie eine Start-Entität und Felder – die Vorschau erscheint danach automatisch.
                    </span>
                </div>
            </p-message>
        }

        @case ('pending') {
            <div class="flex flex-column gap-2" data-test="ql-preview-pending">
                <p-skeleton height="2rem" width="40%"></p-skeleton>
                <p-skeleton height="14rem"></p-skeleton>
                <p-skeleton height="1.5rem" width="60%"></p-skeleton>
            </div>
        }

        @case ('violations') {
            <p-message severity="warn" styleClass="w-full" data-test="ql-preview-violations">
                <div class="flex flex-column gap-2">
                    <span class="font-semibold">Die Abfrage verletzt Regeln und wird nicht ausgeführt.</span>
                    <ul class="m-0 pl-3 flex flex-column gap-1">
                        @for (violation of state.violations; track $index) {
                            <li>
                                <span>{{ violationText(violation) }}</span>
                                <span class="text-color-secondary text-sm ml-2" [pTooltip]="violation.message">
                                    ({{ violation.code }} · {{ violation.path }})
                                </span>
                            </li>
                        }
                    </ul>
                </div>
            </p-message>
        }

        @case ('invalid') {
            <p-message severity="error" styleClass="w-full" data-test="ql-preview-invalid">
                <div class="flex flex-column gap-2">
                    <span class="font-semibold">Das Widget kann mit dieser Zuordnung nicht angezeigt werden.</span>
                    <ul class="m-0 pl-3 flex flex-column gap-1">
                        @for (error of state.widget?.errors; track $index) {
                            <li>{{ error.message }}</li>
                        }
                    </ul>
                </div>
            </p-message>
        }

        @case ('error') {
            <p-message severity="error" styleClass="w-full" [text]="state.errorMessage"></p-message>
        }

        @case ('ready') {

            @if (state.widget?.envBound || state.widget?.sideCarNotUniqueJoins?.length) {
                <div class="flex flex-column gap-2">
                    @if (state.widget?.envBound) {
                        <div class="flex align-items-center gap-2">
                            <p-tag severity="secondary" value="umgebungsgebunden" icon="fa-regular fa-link"></p-tag>
                            <span class="text-sm text-color-secondary">
                                Die Abfrage enthält IDs und lässt sich nicht in andere Umgebungen übertragen.
                            </span>
                        </div>
                    }
                    @if (state.widget?.sideCarNotUniqueJoins?.length) {
                        <p-message severity="warn" styleClass="w-full" data-test="ql-preview-side-car-not-unique">
                            Die externen Zusatzfelder sind nicht eindeutig: Liegen je Datensatz mehrere Werte vor,
                            wird ein beliebiger davon angezeigt.
                        </p-message>
                    }
                </div>
            }

            <p-tabs [value]="activeTab" (valueChange)="selectTab($event)">
                <p-tablist>
                    <p-tab value="widget"><i class="fa-regular fa-chart-simple mr-2"></i>Widget</p-tab>
                    <p-tab value="raw"><i class="fa-regular fa-table mr-2"></i>Rohdaten</p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="widget">
                        @if (activeTab === 'widget' && state.widget?.widgetData) {
                            <div [class.ui-ql-preview-wide-chart]="wideChart" data-test="ql-preview-widget">
                                <mvs-widget [widgetData]="state.widget.widgetData"
                                            (onLoadData)="onWidgetLoaded('widget', $event)">
                                </mvs-widget>
                            </div>
                        }
                    </p-tabpanel>
                    <p-tabpanel value="raw">
                        @if (activeTab === 'raw' && state.raw?.widgetData) {
                            <mvs-widget [widgetData]="state.raw.widgetData"
                                        (onLoadData)="onWidgetLoaded('raw', $event)">
                            </mvs-widget>
                        }
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>

            <!-- status bar -->
            <div class="flex flex-wrap align-items-center gap-3 p-2 border-1 surface-border border-round-lg text-sm"
                 data-test="ql-preview-status">
                @if (currentStatus; as status) {
                    @if (currentBuild?.countAvailable) {
                        <span data-test="ql-preview-count">
                            <i class="fa-regular fa-list mr-1"></i>
                            {{ status.rows }} von {{ status.total ?? '–' }} Zeilen
                        </span>
                    } @else {
                        <span data-test="ql-preview-count">
                            <i class="fa-regular fa-list mr-1"></i>{{ status.rows }} Zeilen · Anzahl nicht verfügbar
                            <span class="text-color-secondary">({{ currentBuild?.countUnavailableReason }})</span>
                        </span>
                    }
                    <span data-test="ql-preview-runtime">
                        <i class="fa-regular fa-stopwatch mr-1"></i>
                        @if (status.runtimeMs != null) {
                            {{ status.runtimeMs }} ms
                            @if (status.runtimeSource === 'client') {
                                <span class="text-color-secondary">(im Browser gemessen)</span>
                            }
                        } @else {
                            Laufzeit unbekannt
                        }
                    </span>
                    <p-tag [value]="'Pipe: ' + currentBuild?.pipe" severity="secondary"></p-tag>
                } @else {
                    <span class="text-color-secondary">
                        <i class="fa-regular fa-hourglass-half mr-1"></i>Daten werden geladen …
                    </span>
                    <p-tag [value]="'Pipe: ' + currentBuild?.pipe" severity="secondary"></p-tag>
                }
            </div>

            @if (currentStatus?.pipeErrors?.length) {
                <p-message severity="error" styleClass="w-full" data-test="ql-preview-pipe-errors">
                    <div class="flex flex-column gap-1">
                        <span class="font-semibold">Die Abfrage wurde mit Fehlern ausgeführt.</span>
                        @for (pipeError of currentStatus?.pipeErrors; track $index) {
                            <span>{{ pipeError }}</span>
                        }
                    </div>
                </p-message>
            }
        }
    }
</div>
